import {
  IMyFilesMediaItem,
  IBaseMediaItem,
  TMediaItemType,
  MediaStatus,
  IUploadMediaItem
} from 'types/media'

import { FIT } from 'consts/canvas'
import { IMAGE_EXTENSIONS } from 'consts/uploadTypes'

import { IDimensions, TScaleMode } from 'types'
import { IMediaItem } from 'types/media'
import { DEFAULT_MEDIA_DURATION, PROJECT_MEDIA_TYPES } from 'consts'
import { ITtsItemData } from 'types/text-to-speech'
import { IAudioItem } from 'types/audio'
import { IUserMediaUploadResponse } from 'types/userMediaUpload'
import { IMediaLibConfig } from 'components/UploadArea/types'

interface ICalculateMediaRectParams {
  mediaDimensions: IDimensions
  mode: TScaleMode
  canvasDimensions: IDimensions
  rotation: number
}

export const calculateMediaRect = ({
  mediaDimensions,
  mode,
  canvasDimensions,
  rotation
}: ICalculateMediaRectParams) => {
  const isRotated = rotation % 180 !== 0

  const mediaWidth = isRotated ? mediaDimensions.height : mediaDimensions.width
  const mediaHeight = isRotated ? mediaDimensions.width : mediaDimensions.height

  const { width: canvasWidth, height: canvasHeight } = canvasDimensions

  const mediaAspectRatio = mediaWidth / mediaHeight
  const canvasAspectRatio = canvasWidth / canvasHeight

  const shouldScaleToWidth =
    mode === FIT
      ? mediaAspectRatio > canvasAspectRatio
      : mediaAspectRatio < canvasAspectRatio

  const scaledDimensions = shouldScaleToWidth
    ? {
        width: canvasWidth,
        height: canvasWidth / mediaAspectRatio
      }
    : {
        width: canvasHeight * mediaAspectRatio,
        height: canvasHeight
      }

  const finalDimensions = isRotated
    ? {
        width: scaledDimensions.height,
        height: scaledDimensions.width
      }
    : scaledDimensions

  const x = (canvasWidth - finalDimensions.width) / 2
  const y = (canvasHeight - finalDimensions.height) / 2

  return {
    ...finalDimensions,
    x,
    y
  }
}

const getMediaItemType = (
  sourceType: IMyFilesMediaItem['sourceType'],
  mime: string
): TMediaItemType => {
  const sourceTypeMap: Record<string, TMediaItemType> = {
    'ai-image': PROJECT_MEDIA_TYPES.aiImage,
    'ai-video': PROJECT_MEDIA_TYPES.aiVideo
  }
  const mimeType = mime.split('/')[0]

  if (sourceType && sourceType in sourceTypeMap) {
    return sourceTypeMap[sourceType]
  }

  return mimeType === 'image'
    ? PROJECT_MEDIA_TYPES.image
    : PROJECT_MEDIA_TYPES.video
}

const getVideoDurationMeta = (
  file: IMyFilesMediaItem,
  isImage: boolean
): Partial<IMediaItem['meta']> => {
  if (isImage || !file?.duration) {
    return {}
  }

  return {
    originalDuration: file.duration,
    trimStart: 0,
    previewPath: file.thumbnailVideo,
    volume: 100
  }
}

const createMediaItem = (
  file: IMyFilesMediaItem,
  mediaRect: ReturnType<typeof calculateMediaRect>,
  startTime: number,
  mimeType: string
): IMediaItem => {
  const isImage = mimeType.startsWith('image/')
  const itemType = getMediaItemType(file.sourceType, mimeType)
  const duration = file.duration || DEFAULT_MEDIA_DURATION
  const assetId = file?.assetId ? file.assetId.toString() : file.id.toString()

  return {
    id: crypto.randomUUID(),
    assetId,
    type: itemType,
    height: mediaRect.height,
    width: mediaRect.width,
    rotation: 0,
    duration,
    startTime,
    zIndex: 0,
    coordinates: {
      x: mediaRect.x,
      y: mediaRect.y
    },
    meta: {
      path: file.filePath,
      originalHeight: file.height,
      originalWidth: file.width,
      status: MediaStatus.OK,
      ...getVideoDurationMeta(file, isImage)
    }
  }
}

export const calculateInsertionStartTime = (
  playheadTime: number,
  existingItems: IMediaItem[],
  newItemsTotalDuration: number
): number => {
  if (!existingItems.length) return playheadTime

  const items = sortMediaItemsByStartTime(existingItems)

  const spaceToNextItem = items.reduce((spaceToNextItem, item) => {
    if (spaceToNextItem !== Infinity) return spaceToNextItem

    const itemStart = item.startTime
    const itemEnd = item.startTime + item.duration

    // playhead inside item → insert at end
    if (playheadTime >= itemStart && playheadTime < itemEnd) {
      return 0
    }

    // first item after playhead → defines available space
    if (itemStart >= playheadTime) {
      return itemStart - playheadTime
    }

    return spaceToNextItem
  }, Infinity)

  // not enough space → insert at end
  if (spaceToNextItem === 0 || spaceToNextItem < newItemsTotalDuration) {
    return getLastMediaItemEndTime(items)
  }

  return playheadTime
}

export const convertMediaLibraryItemsToMediaItems = (
  files: IMyFilesMediaItem[],
  insertionStartTime: number = 0,
  canvasDimensions: IDimensions
): IMediaItem[] => {
  const items: IMediaItem[] = []
  let currentStartTime = insertionStartTime

  for (const file of files) {
    const mediaRect = calculateMediaRect({
      mediaDimensions: { width: file.width, height: file.height },
      mode: FIT,
      canvasDimensions,
      rotation: 0
    })

    const item = createMediaItem(file, mediaRect, currentStartTime, file.mime)

    items.push(item)
    currentStartTime += item.duration
  }

  return items
}

export const captureVideoFrame = async (
  videoUrl: string,
  timestamp: number = 0
): Promise<string> => {
  const video = document.createElement('video')
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  video.crossOrigin = 'anonymous'
  video.src = videoUrl

  await new Promise((resolve, reject) => {
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.currentTime = timestamp
    }
    video.onseeked = resolve
    video.onerror = reject
  })

  context?.drawImage(video, 0, 0, canvas.width, canvas.height)
  const frame = canvas.toDataURL('image/jpeg')

  video.remove()
  canvas.remove()

  return frame
}

export const convertBaseItemToMediaItem = (
  mediaItem: IBaseMediaItem,
  insertionStartTime: number = 0,
  canvasDimensions: IDimensions
) => {
  const startTime = insertionStartTime
  const width = mediaItem.originalWidth || 0
  const height = mediaItem.originalHeight || 0

  const mediaRect = calculateMediaRect({
    mediaDimensions: { width, height },
    mode: FIT,
    canvasDimensions,
    rotation: 0
  })

  const getDurationMeta = () => {
    if (
      mediaItem.type !== PROJECT_MEDIA_TYPES.image &&
      mediaItem.type !== PROJECT_MEDIA_TYPES.aiImage &&
      mediaItem.duration
    ) {
      return {
        originalDuration: mediaItem.duration,
        trimStart: 0,
        volume: 100
      }
    }
    return {}
  }

  return {
    id: mediaItem.id || crypto.randomUUID(),
    assetId: mediaItem.assetId || crypto.randomUUID(),
    type: mediaItem.type,
    height: mediaRect.height,
    width: mediaRect.width,
    rotation: 0,
    duration: mediaItem.duration,
    startTime,
    zIndex: 0,
    coordinates: {
      x: mediaRect.x,
      y: mediaRect.y
    },
    meta: {
      path: mediaItem.path,
      originalHeight: mediaRect.height,
      originalWidth: mediaRect.width,
      status: mediaItem.status || MediaStatus.OK,
      assetParentId: mediaItem.assetParentId,
      ...getDurationMeta()
    }
  }
}

export const mergeRecentlyUsedItems = (
  existing: IBaseMediaItem[],
  newItems: IMediaItem[]
) => {
  const storageItems: IBaseMediaItem[] = newItems.map(item => ({
    fileName: item.meta.path?.split('/').pop() || '',
    assetId: item.assetId,
    type: item.type,
    originalWidth: item.meta.originalWidth,
    originalHeight: item.meta.originalHeight,
    path: item.meta?.path,
    duration:
      item.type === PROJECT_MEDIA_TYPES.image
        ? DEFAULT_MEDIA_DURATION
        : item.duration
  }))

  const uniques = storageItems.filter(
    item => !existing.some(i => i.path === item.path)
  )
  const merged = [...uniques, ...existing]

  return merged.splice(0, 30)
}

export const calculateTotalMediaDuration = (
  mediaItems: IMediaItem[]
): number => {
  return mediaItems.reduce(
    (acc: number, item: IMediaItem) =>
      acc + (item.duration || DEFAULT_MEDIA_DURATION),
    0
  )
}

export const convertTtsItemIntoAudioItem = (
  ttsItem: ITtsItemData
): IAudioItem => ({
  id: crypto.randomUUID(),
  assetId: `${ttsItem.id}`,
  duration: ttsItem.duration,
  mediaStart: 0,
  mediaDuration: ttsItem.duration,
  projectStart: 0,
  mediaPath: ttsItem.filePath,
  title: ttsItem.text,
  type: 'TTS',
  volume: 100,
  lineIndex: 0
})

export const isVisualMediaItem = (type: string | null): boolean => {
  const visualMediaTypes = Object.values(PROJECT_MEDIA_TYPES) as string[]
  return type ? visualMediaTypes.includes(type) : false
}

export const isAiVideoPending = (item: IMediaItem): boolean => {
  return (
    item.type === PROJECT_MEDIA_TYPES.aiVideo &&
    item.meta.status === MediaStatus.PENDING
  )
}

export const mapMediaInputAndOutput = (
  inputMediaFiles: Array<IUploadMediaItem>,
  data: IUserMediaUploadResponse
) => {
  const images = inputMediaFiles.filter(
    media => media.type === PROJECT_MEDIA_TYPES.image
  )
  const videos = inputMediaFiles.filter(
    media => media.type === PROJECT_MEDIA_TYPES.video
  )

  return [
    ...images.map((image, index) => ({
      id: String(image.id),
      filePath: data.images![index].filePath,
      width: data.images![index].width,
      height: data.images![index].height
    })),
    ...videos.map((video, index) => ({
      id: String(video.id),
      filePath: data.videos![index].filePath,
      width: data.videos![index].width,
      height: data.videos![index].height,
      duration: data.videos![index].duration,
      smallFilePath: data.videos![index].thumbnailVideo
    }))
  ]
}

export const isImagePath = (path?: string | null): boolean => {
  if (!path) return false
  const extension = path.split('.').pop()
  if (!extension) return false
  return IMAGE_EXTENSIONS.includes(
    extension as (typeof IMAGE_EXTENSIONS)[number]
  )
}

export const calculateTotalFilesSize = (
  files: Array<File | IMyFilesMediaItem>
): number =>
  files.reduce(
    (total, file) =>
      total + (file instanceof File ? file.size : file.fileSize || 0),
    0
  )

/**
 * Source trim start in seconds (where playback begins in the file).
 * Matches v2ww `convertVideoMetadata`: `meta.trimStart` (seconds), else timeline `startTime`.
 */
export const getMediaSourceTrimStartSeconds = (item: IMediaItem): number =>
  item.meta.trimStart ?? item.startTime

export const getLastMediaItemEndTime = (mediaItems: IMediaItem[]) => {
  return mediaItems.reduce(
    (max, item) => Math.max(max, item.startTime + item.duration),
    0
  )
}

export const sortMediaItemsByStartTime = (mediaItems: IMediaItem[]) =>
  [...mediaItems].sort((a, b) => a.startTime - b.startTime)

export const openMediaLib = (config: IMediaLibConfig) => {
  const defaultConfig = {
    fileType: 'image',
    onClose: window.closeMediaLib,
    mediaLibUnmountCb: window.closeMediaLib,
    maxFileImportCount: 1,
    isVisible: true,
    onChoose: () => {}
  }
  window.openMediaLib({ ...defaultConfig, ...config })
}
